import { Box } from "@chakra-ui/react";
import { FC, useRef, useState } from "react";
import {
  MapContainer,
  Marker,
  Popup,
  TileLayer,
  useMap,
  useMapEvents,
} from "react-leaflet";
import icon from "leaflet/dist/images/marker-icon.png";
import iconShadow from "leaflet/dist/images/marker-shadow.png";
import {
  DragEndEventHandlerFn,
  LatLng,
  Icon,
  Map as LeafletMap,
} from "leaflet";
import "./mapEditor.css";
import { useEffect } from "react";
import React from "react";
import {
  useMapEditorAddressLookup,
  MapEditorAddressLookupType,
  isAddressLookupReady,
} from "./useMapEditorLookUp";

type mapEditorProps = {
  width?: number | string;
  height?: number | string;
  markerName?: string;
  coordValue?: { Lat: number; Lng: number };
  onChange: (e: { Lat: number; Lng: number }) => void;
  addressLookup?: MapEditorAddressLookupType;
  addressLookupError?: (e: any) => void;
};

type MyComponentProps = {
  setPosition(arg: LatLng): void;
};

/** Modal/layout timing: Leaflet sizes before the container has final dimensions (AR1-1). */
function InvalidateSizeWhenReady() {
  const map = useMap();
  useEffect(() => {
    const sync = () => {
      map.invalidateSize({ animate: false });
    };
    sync();
    let innerRaf = 0;
    const outerRaf = requestAnimationFrame(() => {
      innerRaf = requestAnimationFrame(sync);
    });
    const t1 = window.setTimeout(sync, 100);
    const t2 = window.setTimeout(sync, 350);
    return () => {
      cancelAnimationFrame(outerRaf);
      cancelAnimationFrame(innerRaf);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [map]);
  return null;
}

/*
  MapEditor component 
  Properties
    width?      sets the width of the map component
    height?     set the height of the map component
    markerName? name of the marker   
    coordValue  {Lat: xxx, Lng:xxx} location of the marker.
                in case blank, no marker is shown, map is position in central of europe zoom 4
                in case set, marker will be set and map will be centered and zoomed on the position
    onChange    Event handler for when the marker is moved or reset with click
    addressLookup Either pass a string, either pass an address component
                Based on the address, the position will be fetched from OSM and
                if found, the marker is set
    addressLookupError In case of a network or other error occurs while fetching
                coordinates of an address
*/
export const MapEditor: FC<mapEditorProps> = React.memo(
  ({
    width,
    height,
    markerName,
    coordValue,
    onChange,
    addressLookup,
    addressLookupError,
  }) => {
    /** MapContainer (react-leaflet v5) forwards ref to the real Leaflet Map once context exists. */
    const mapRef = useRef<LeafletMap | null>(null);

    const [position, setPosition] = useState<LatLng | null>(null);
    const [center] = useState(
      coordValue
        ? new LatLng(coordValue.Lat, coordValue.Lng)
        : new LatLng(47.03, 7.42)
    );
    const markerRef = useRef<typeof Marker | any>(null);
    const { LookUpAddress } = useMapEditorAddressLookup();
    const dragMarkerHandler: DragEndEventHandlerFn = () => {
      const marker = markerRef.current;
      if (marker) {
        const val = marker.getLatLng();
        const newVal = { Lat: val.lat, Lng: val.lng };
        setPosition(marker.getLatLng());
        onChange(newVal);
      }
    };
    const defaultIcon = new Icon({
      iconUrl: icon,
      shadowUrl: iconShadow,
    });

    function MyComponent({ setPosition }: MyComponentProps) {
      const map = useMapEvents({
        click: (e) => {
          const newVal = { Lat: e.latlng.lat, Lng: e.latlng.lng };
          setPosition(e.latlng);
          map.flyTo(e.latlng);
          onChange(newVal);
        },
      });
      return null;
    }

    function setCoord(lat: number, lng: number) {
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        return;
      }
      const newPos = new LatLng(lat, lng);
      setPosition(newPos);

      const applyView = () => {
        const map = mapRef.current;
        if (!map) {
          return;
        }
        map.invalidateSize({ animate: false });
        map.setView(newPos, 15, { animate: false });
      };

      applyView();
      requestAnimationFrame(applyView);
      [0, 16, 50, 100, 200, 400, 600, 1000].forEach((ms) => {
        window.setTimeout(applyView, ms);
      });
      window.setTimeout(() => {
        mapRef.current?.whenReady(applyView);
      }, 0);
    }
    async function addressLookupPassed() {
      try {
        const result = await LookUpAddress(addressLookup!);
        if (result) {
          setCoord(result.Lat, result.Lng);
          onChange({ Lat: result.Lat, Lng: result.Lng });
        } else {
          if (addressLookupError) {
            addressLookupError("no address found");
          }
        }
      } catch (ex) {
        if (addressLookupError) {
          addressLookupError(ex);
        }
      }
    }

    // in case new value is passed, reset the centering
    useEffect(() => {
      if (coordValue) {
        if (
          coordValue.Lat !== position?.lat ||
          coordValue.Lng !== position?.lng
        ) {
          setCoord(coordValue.Lat, coordValue.Lng);
        }
      }
    }, [coordValue]);

    // in case adressLookup is passed, get the coordinates if possible
    useEffect(() => {
      if (isAddressLookupReady(addressLookup)) {
        addressLookupPassed();
      }
    }, [addressLookup]);

    return (
      <Box w={width} h={height}>
        <MapContainer
          ref={mapRef}
          center={center}
          zoom={coordValue ? 15 : 8}
          scrollWheelZoom={true}
          className="leaflet-container"
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <InvalidateSizeWhenReady />
          <MyComponent setPosition={setPosition} />
          {position ? (
            <Marker
              draggable={true}
              eventHandlers={{
                dragend: dragMarkerHandler,
              }}
              ref={markerRef}
              position={[position.lat, position.lng]}
              data-testid="testmarker"
              icon={defaultIcon}
            >
              <Popup>{markerName}</Popup>
            </Marker>
          ) : null}
        </MapContainer>
      </Box>
    );
  }
);
